import React, { useState, useRef, useEffect } from 'react';
import './EditableText.css';

export interface EditableTextProps {
  text: string;
  onSave: (newText: string) => Promise<void>;
  onCancel?: () => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  className?: string;
  canEdit?: boolean;
  editButtonTitle?: string;
}

const EditableText: React.FC<EditableTextProps> = ({
  text,
  onSave,
  onCancel,
  placeholder = "Введите текст...",
  maxLength = 1000,
  disabled = false,
  className = '',
  canEdit = true,
  editButtonTitle = "Редактировать"
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(text);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Сброс состояния при изменении текста
  useEffect(() => {
    setEditText(text);
    setError(null);
  }, [text]);

  // Автофокус при начале редактирования
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(editText.length, editText.length);
    }
  }, [isEditing, editText.length]);

  const handleStartEdit = () => {
    if (disabled || !canEdit) return;
    setIsEditing(true);
    setEditText(text);
    setError(null);
  };

  const handleSave = async () => {
    if (!editText.trim() || isSaving) return;

    try {
      setIsSaving(true);
      setError(null);
      await onSave(editText.trim());
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error saving text:', err);
      setError(err.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditText(text);
    setError(null);
    if (onCancel) {
      onCancel();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className={`editable-text editing ${className}`}>
        {error && (
          <div className="editable-text-error">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}
        <div className="editable-text-input-container">
          <textarea
            ref={textareaRef}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={placeholder}
            className="editable-text-input"
            rows={Math.max(2, Math.min(10, editText.split('\n').length))}
            maxLength={maxLength}
            disabled={isSaving}
          />
          <div className="editable-text-actions">
            <button
              className="editable-text-btn editable-text-btn--save"
              onClick={handleSave}
              disabled={!editText.trim() || isSaving}
              title="Сохранить (Enter)"
            >
              {isSaving ? (
                <i className="ri-loader-4-line ri-spin"></i>
              ) : (
                <i className="ri-check-line"></i>
              )}
            </button>
            <button
              className="editable-text-btn editable-text-btn--cancel"
              onClick={handleCancel}
              disabled={isSaving}
              title="Отменить (Esc)"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`editable-text ${className}`}>
      <div className="editable-text-content">
        <div className="editable-text-display">
          {text}
        </div>
        {canEdit && !disabled && (
          <button
            className="editable-text-edit-btn"
            onClick={handleStartEdit}
            title={editButtonTitle}
          >
            <i className="ri-edit-line"></i>
          </button>
        )}
      </div>
    </div>
  );
};

export default EditableText;
